import React, { useState, useEffect, useCallback } from "react";
import { dataGridSurface } from "../common/tableStyles";
import {
  Avatar,
  Box,
  Chip,
  styled,
  Tooltip,
  MenuItem,
  Select,
  TextField,
  IconButton,
  InputAdornment,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import UserActions from "../actions/UserActions";
import UserService from "../../services/UserService";
import IUser from "../../entities/IUser";
import { unwrapData, unwrapList, getErrorMessage } from "../../services/apiUtils";
import { getUserId } from "../../services/userUtils";
import DataGridLoadingOverlay from "../common/DataGridLoadingOverlay";

const USER_SEARCH_FIELDS: Array<{ label: string; key: string }> = [
  { label: "Email", key: "email" },
  { label: "Name", key: "name" },
  { label: "Email (native)", key: "emailNative" },
  { label: "Vendor Name", key: "vendorName" },
  { label: "First Name", key: "firstName" },
];

const runUserSearch = (key: string, value: string) => {
  switch (key) {
    case "email":
      return UserService.getUserByEmailId(value);
    case "name":
      return UserService.getUserByName(value);
    case "emailNative":
      return UserService.getUserByEmailAddressNative(value);
    case "vendorName":
      return UserService.findByVendorName(value);
    case "firstName":
      return UserService.findByFirstName(value);
    default:
      return UserService.getUsers();
  }
};


const StyledTable = styled(Box)(({ theme }) => ({
  ...dataGridSurface(theme),
  margin: "24px 0 0 0",
  height: 560,
}));

const getInitials = (name?: string | null): string => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  const initials = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : trimmed.slice(0, 2);
  return initials.toUpperCase();
};

const UsersTable: React.FC = () => {
  const [userData, setUserData] = useState<Array<IUser>>([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [searchField, setSearchField] = useState(USER_SEARCH_FIELDS[0].key);
  const [searchInput, setSearchInput] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setSearchError(null);
    UserService.getUsers()
      .then((res: any) => {
        setUserData(unwrapList<IUser>(res));
      })
      .catch(() => {
        setUserData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const runSearch = () => {
    const value = searchInput.trim();
    if (!value) {
      setSearchActive(false);
      loadUsers();
      return;
    }
    setLoading(true);
    setSearchActive(true);
    setSearchError(null);
    runUserSearch(searchField, value)
      .then((res: any) => {
        const data = unwrapData(res);
        const list = Array.isArray(data) ? data : data ? [data] : [];
        setUserData(list as Array<IUser>);
        if (list.length === 0) {
          setSearchError("No matching user found.");
        }
      })
      .catch((err: any) => {
        setUserData([]);
        setSearchError(getErrorMessage(err, "Could not search users."));
      })
      .finally(() => setLoading(false));
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchActive(false);
    setSearchError(null);
    loadUsers();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearch();
    }
  };

  const columns: GridColDef[] = [
    { field: "userId", headerName: "ID", flex: 0.4, valueGetter: (params) => getUserId(params.row) },
    {
      field: "avatar",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 64,
      renderCell: (params) => (
        <Tooltip title={params.row.name || params.row.emailId}>
          <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
            {getInitials(params.row.name)}
          </Avatar>
        </Tooltip>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
      valueGetter: (params) => params.row.name || "-",
      renderCell: (params) => (
        <Tooltip title={params.value || "-"}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      valueGetter: (params) => params.row.email || params.row.emailId || "-",
      renderCell: (params) => (
        <Tooltip title={params.value || "-"}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 0.8,
      valueGetter: (params) => params.row.phone || params.row.mobile || "-",
    },
    {
      field: "gender",
      headerName: "Gender",
      flex: 0.6,
      valueGetter: (params) => (params.row.gender ? String(params.row.gender) : "-"),
      renderCell: (params) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "userRole",
      headerName: "Role",
      flex: 0.7,
      valueGetter: (params) => params.row.userRole?.roleName || "-",
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === "ADMIN" ? "secondary" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "enabled",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.enabled ? "Active" : "Inactive"}
          color={params.row.enabled ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 170,
      type: "actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => <UserActions user={params.row} onDeleted={loadUsers} />,
    },
  ];

  return (
    <StyledTable>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          mb: 2,
        }}
      >
        <Select
          size="small"
          value={searchField}
          onChange={(e) => setSearchField(e.target.value as string)}
          sx={{ minWidth: 170, bgcolor: "#fff" }}
        >
          {USER_SEARCH_FIELDS.map((f) => (
            <MenuItem key={f.key} value={f.key}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder={`Search by ${USER_SEARCH_FIELDS.find((f) => f.key === searchField)?.label || "..."} — press Enter`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ minWidth: 280, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small" onClick={runSearch} edge="start">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchActive && (
          <Button size="small" onClick={clearSearch}>
            Clear Search
          </Button>
        )}
        {searchError && (
          <Box component="span" sx={{ color: "error.main", fontSize: 13 }}>
            {searchError}
          </Box>
        )}
      </Box>
      <DataGrid
        sx={{ backgroundColor: "transparent" }}
        rows={userData}
        columns={columns}
        getRowId={(row: any) => getUserId(row)}
        components={{ LoadingOverlay: DataGridLoadingOverlay }}
        loading={loading}
        pageSize={30}
        rowsPerPageOptions={[30]}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
      />
    </StyledTable>
  );
};

export default UsersTable;
